import type { MissionStatus } from "@/components/landing/StatusPills";
import { firstSceneAuthPath, subscribeAuthPath } from "@/lib/firstScene";

export const SCENE_HREF = firstSceneAuthPath();
export const SUBSCRIBE_HREF = subscribeAuthPath("signup");
export const SIGNIN_HREF = subscribeAuthPath("signin");

export const LANDING_COPY = {
  eyebrow: "Prática de inglês em voz",
  headline: ["Fale inglês nas situações reais.", "Assine no site."],
  headlineAccent: "R$5,99 por mês.",
  sub: "Aeroporto, restaurante, reunião. Cinco minutos de ensaio com a IA. Depois você vê no que travou. Se o treino foi suficiente, o tradutor em tempo real entra na conversa de verdade.",
  cta: "Assinar",
  ctaSecondary: "Já tenho conta",
  micro: "Cancele quando quiser. Pagamento seguro no Stripe.",
  signIn: "Entrar",
  navScenes: "Situações",
  navHow: "Como é",
  navLive: "Tradutor",
  navPrice: "Preço",
  scenesEyebrow: "Situações do dia a dia",
  scenesTitle: "O mapa de “já me viro”.",
  scenesSub: "Aeroporto, restaurante, reunião, hotel, médico, Uber. Você treina o que vai viver — não uma lista de palavras.",
  howEyebrow: "Como é",
  howTitle: "Ensaia. Vê o que ficou. Usa na conversa.",
  howSub: "Você já é o passageiro. Cinco minutos. Sem aula e sem escrever o que vai falar.",
  liveEyebrow: "Na conversa de verdade",
  liveTitle: "Se o treino foi suficiente, o tradutor entra ao vivo.",
  liveBody: "Ensaiou a cena. Quando já se vira o bastante, a Ajuda traduz em tempo real: você fala, o outro fala, o Talki traduz na hora.",
  retainEyebrow: "Por que o inglês fica",
  retainTitle: "Fica na cabeça o que você vive.",
  retainSub: "Treino na situação real. Você vê o que deu conta e repete só o que travou.",
  proofEyebrow: "Na vida real",
  proofTitle: "A mesma situação, dois dias depois.",
  pricingEyebrow: "Preço",
  pricingTitle: "Uma assinatura. Tudo liberado.",
  pricingSub: "Pague no site. O mesmo Pro vale no computador e no celular, com o mesmo login.",
  pricingName: "Talki Pro",
  pricingPeriod: "/mês",
  pricingTrust: ["Cancele quando quiser", "Pagamento no Stripe", "Mesmo login no celular"],
  closeTitle: "Comece a falar. R$5,99/mês.",
  closeMicro: "Cancele quando quiser. Sem compromisso.",
  closeCta: "Assinar agora",
  footer: "Feito no Brasil · Talki",
} as const;

export const DEBRIEF_CARD = {
  title: "No aeroporto",
  handledLabel: "Se virou em",
  handled: "pedir o cartão de embarque",
  stuckLabel: "Travou em",
  stuck: "voo lotado",
  confidence: "Você se sente confiante?",
  coach: "Vamos falar de novo só o voo lotado.",
} as const;

export const PALCO_HERO_SNAPSHOT = {
  partnerName: "Maya",
  partnerLabel: "Maya — atendente do balcão",
  stageArt: "/characters/maya.webp",
  goal: "Sair com o cartão de embarque",
  partnerOpen: "Good morning. May I see your passport, please?",
  helper: "Fale agora · ajuda em português se precisar",
} as const;

export type LandingSceneId = "aeroporto" | "restaurante" | "reuniao" | "hotel" | "medico" | "uber";

export type LandingScene = {
  id: LandingSceneId;
  name: string;
  short: string;
  line: string;
  status: MissionStatus;
  tint: string;
};

export const LANDING_SCENES: LandingScene[] = [
  {
    id: "aeroporto",
    name: "Aeroporto",
    short: "Aeroporto",
    line: "Pedir o cartão de embarque",
    status: "quase",
    tint: "from-blue-100 to-blue-50",
  },
  {
    id: "restaurante",
    name: "Restaurante",
    short: "Rest.",
    line: "Pedir a conta",
    status: "me-viro",
    tint: "from-[#FFEDD5] to-[#FFF7ED]",
  },
  {
    id: "reuniao",
    name: "Reunião",
    short: "Reunião",
    line: "Contar o que aconteceu ontem",
    status: "ainda-nao",
    tint: "from-[#E0E7FF] to-[#EEF2FF]",
  },
  {
    id: "hotel",
    name: "Hotel",
    short: "Hotel",
    line: "Confirmar a reserva",
    status: "ainda-nao",
    tint: "from-[#FCE7F3] to-[#FDF2F8]",
  },
  {
    id: "medico",
    name: "Médico",
    short: "Médico",
    line: "Dizer onde dói",
    status: "ainda-nao",
    tint: "from-[#DCFCE7] to-[#F0FDF4]",
  },
  {
    id: "uber",
    name: "Uber",
    short: "Uber",
    line: "Confirmar o destino",
    status: "ainda-nao",
    tint: "from-[#E0F2FE] to-[#F0F9FF]",
  },
];

/** @deprecated use LANDING_SCENES */
export const MISSIONS = LANDING_SCENES;

export const STEPS = [
  {
    title: "Entra na conversa",
    body: "Você já é o passageiro. O objetivo está pronto. Sem escolher nível, sem escrever o que vai falar.",
    accent: "bg-blue-50 text-blue-600",
  },
  {
    title: "Fala 3 ou 4 vezes",
    body: "Do outro lado, alguém responde. Se travar, uma dica em português. A conversa continua.",
    accent: "bg-orange-50 text-orange-700",
  },
  {
    title: "Vê onde já se vira",
    body: "O que funcionou. O que ainda trava. Repete só o difícil — ou vai para o mapa.",
    accent: "bg-blue-50 text-blue-600",
  },
] as const;

export const RETAIN_POINTS = [
  {
    title: "Você vive a situação",
    body: "Não é lista de palavras. É o aeroporto, a mesa, a reunião. O que aconteceu fica na cabeça.",
  },
  {
    title: "Você vê o que ficou",
    body: "O que deu certo. O que ainda trava. Sem nota, sem medalha — um mapa de confiança.",
  },
  {
    title: "Repete só o que travou",
    body: "Não recomeça tudo. Volta no voo lotado, na fila, no bloqueio.",
  },
  {
    title: "Quando bastou, traduz ao vivo",
    body: "Se o treino foi suficiente, você usa o tradutor em tempo real na conversa — não só no ensaio.",
  },
] as const;

export const TESTIMONIALS = [
  {
    quote: "Treinei o aeroporto na terça. Na quinta pedi o cartão de embarque sem traduzir.",
    name: "Marina, São Paulo",
    meta: "Viagem marcada · mesma situação",
  },
  {
    quote: "Parei de ficar mudo na reunião. Treinei duas vezes o que aconteceu ontem e falei de verdade.",
    name: "Rafael, Curitiba",
    meta: "Trabalho · mesma situação",
  },
  {
    quote: "Pedi a conta e um prato diferente sem abrir o tradutor. Cinco minutos no restaurante.",
    name: "Camila, Recife",
    meta: "Jantar fora · mesma situação",
  },
] as const;

export const HERO_CHIPS = [
  { label: "Restaurante", tone: "orange" as const },
  { label: "Uber", tone: "blue" as const },
  { label: "Reunião", tone: "blue" as const },
] as const;

